import styled from '@emotion/styled';
import type { ObjectId } from '@racemap/sdk/schema/base';
import type { GPS_MODE, POWER_KEY_MODE } from '@racemap/sdk/schema/trackers';
import type { Immutable } from 'immer';
import { type FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PowerKeyModeHint } from 'src/components/BasicComponents/PowerKeyModeHint/PowerKeyModeHint';
import { SimCardStatusBadge } from 'src/components/SimCardsManager';
import { useIsAdmin, useIsMobile, useIsReseller } from '../../../lib/customHooks';
import { useStore } from '../../../store/reducers';
import { isManagedSampleTracker } from '../../../store/trackers/trackers_helper';
import {
  type ManagedSampleTracker,
  type ManagedTracker,
  TrackerColumnKeys,
  type TrackerTags,
} from '../../../store/trackers/trackers_reducers';
import { Columns, DataTable } from '../../DataTable';
import { TrackerCollumnsVisibilityControlButton } from '../../DataTable/TrackerCollumnsVisibilityControlButton';
import { MessageFilters } from '../Filter/FilterTreeFunctions';
import { GPSModeLabels, PowerKeyModeLabels } from '../TrackerSettingsInfos';
import TrackerColumns from './Columns';

interface Props {
  trackers: Immutable<Array<ManagedTracker | ManagedSampleTracker>>;
  noDataPlaceholder?: React.ReactNode;
  selectedTrackerIds: Immutable<Set<string>>;
  onSelectionChange: (newTrackerIds: Immutable<Set<string>>) => void;
  dragElementType: string;
  disabledTrackerIds?: Immutable<Set<string>>;
}

export const TrackerTable: FC<Props> = ({
  trackers,
  noDataPlaceholder,
  selectedTrackerIds,
  onSelectionChange,
  dragElementType,
  disabledTrackerIds,
}) => {
  const { selectedCols } = useStore((s) => s.trackers);
  const inspectedTrackerId = useStore((s) => s.trackers.inspectedTrackerId);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const trackerHasIndexInformation = trackers.some(isManagedSampleTracker);
  const { showShippingCol } = useStore((s) => s.trackers);
  const { shippingInfo } = useStore((s) => s.trackers);

  const handleFocusRowChange = (rowId: ObjectId | null) => {
    if (rowId != null) {
      navigate({
        pathname: location.pathname,
        search: `?inspectedTracker=${rowId.toHexString()}`,
      });
    } else {
      navigate({ pathname: '/admin/trackers', search: '' });
    }
  };

  const isAdmin = useIsAdmin();
  const isReseller = useIsReseller();

  const shippingCol = TrackerColumns.shippingColumn({ shippingInfo });

  const trackerTags: Immutable<TrackerTags> = useStore((s) => s.trackers.trackerTags);
  const { updateTrackerTag } = useStore((s) => s.trackers.actions);

  const columns = [
    trackerHasIndexInformation
      ? {
          ...Columns.sortingColumn({
            dataKey: TrackerColumnKeys.INDEX,
            style: { justifyContent: 'center' },
            label: '#',
            minWidth: 50,
            width: 50,
            cellRenderer: (props) => <span>{props.cellData + 1}</span>,
          }),
        }
      : Columns.rowIndexColumn(),

    Columns.sortingColumn({
      dataKey: TrackerColumnKeys.NAME,
      style: { justifyContent: 'flex-start' },
      label: 'Name',
      minWidth: isMobile ? 90 : 130,
    }),

    Columns.sortingColumn({
      dataKey: TrackerColumnKeys.TYPE,
      label: 'Type',
      minWidth: 50,
    }),

    TrackerColumns.onlineColumn(),
    TrackerColumns.locationColumn(),
    TrackerColumns.lastMessageColumn(),

    Columns.fromNowColumn({
      dataKey: TrackerColumnKeys.LAST_DATA,
      label: 'Last data',
      style: { justifyContent: 'center' },
      minWidth: 110,
      hint: 'Elapsed time since RACEMAP received the last information from the device which can be a geolocation, a heartbeat, or another message from the device.',
    }),

    TrackerColumns.messagesCountColumn({
      countType: MessageFilters.WITH_ERROR_MESSAGES,
      colLabel: 'ERR',
      colTitle: 'Error messages',
      dataKey: TrackerColumnKeys.ERR,
      hint: 'Number of messages that failed to send, were not acknowledged even though they should have been, or have an incorrect message format.',
    }),

    TrackerColumns.messagesCountColumn({
      countType: MessageFilters.WITH_PENDING_MESSAGES,
      colLabel: 'PEN',
      colTitle: 'Pending messages',
      dataKey: TrackerColumnKeys.PEN,
    }),

    TrackerColumns.messagesCountColumn({
      countType: MessageFilters.WITH_SCHEDULED_MESSAGES,
      colLabel: 'SCH',
      colTitle: 'Scheduled messages',
      dataKey: TrackerColumnKeys.SCH,
    }),

    Columns.sortingColumn({
      minWidth: 100,
      dataKey: TrackerColumnKeys.FIRMWARE,
      style: { justifyContent: 'flex-start' },
      label: 'Firmware',
      columnData: { hiddenOnDefault: true, cellDataFallback: '--' },
    }),

    Columns.sortingColumn({
      minWidth: 110,
      dataKey: TrackerColumnKeys.MAIN_SERVER_IP,
      label: 'Domain',
      columnData: { hiddenOnDefault: true, cellDataFallback: '--' },
      hint: 'IP address or domain name of the server to which the device is connected.',
    }),

    Columns.sortingColumn({
      minWidth: 100,
      dataKey: TrackerColumnKeys.MAIN_SERVER_PORT,
      label: 'Port',
      columnData: { hiddenOnDefault: true, cellDataFallback: '--' },
      hint: 'Port of the server to which the device is connected.',
    }),

    TrackerColumns.batteryColumn(),
    TrackerColumns.activationColumn(),
    TrackerColumns.tagsColumn({ trackerTags, updateTrackerTag }),
    TrackerColumns.movementColumn(),
    TrackerColumns.nonMovementColumn(),

    Columns.sortingColumn({
      minWidth: 100,
      dataKey: TrackerColumnKeys.GPS_MODE,
      label: 'GPS Mode',
      columnData: { hiddenOnDefault: true, cellDataFallback: '--' },
      cellRenderer: (props) => <span>{GPSModeLabels[props.cellData as GPS_MODE] || '--'}</span>,
    }),

    Columns.sortingColumn({
      minWidth: 150,
      dataKey: TrackerColumnKeys.REPORT_DISTANCE,
      label: 'Report distance',
      columnData: { hiddenOnDefault: true, cellDataFallback: '--' },
      cellRenderer: (props) =>
        props.cellData !== null ? <span>{`${props.cellData} m`}</span> : null,
    }),

    TrackerColumns.infoReportColumn(),
    TrackerColumns.heartbeatColumn(),
    TrackerColumns.serverACKColumn(),
    TrackerColumns.chartColumn(),
    TrackerColumns.sosColumn(),

    Columns.sortingColumn({
      minWidth: 150,
      dataKey: TrackerColumnKeys.POWER_KEY_MODE,
      label: 'Power key mode',
      columnData: { hiddenOnDefault: true, cellDataFallback: '--' },
      hint: <PowerKeyModeHint />,
      cellRenderer: (props) => (
        <span>{PowerKeyModeLabels[props.cellData as POWER_KEY_MODE] || '--'}</span>
      ),
    }),

    Columns.sortingColumn({
      minWidth: 100,
      dataKey: TrackerColumnKeys.SIM_CARD_STATUS,
      label: 'SIM-Card',
      columnData: { hiddenOnDefault: true, cellDataFallback: '--' },
      cellRenderer: (props) => <SimCardStatusBadge status={props.cellData} />,
    }),
  ];

  if (showShippingCol && (isAdmin || isReseller)) {
    columns.push(shippingCol);
  }

  const columnLabels = columns.map((child) => ({
    label: child.props.label?.toString() || child.props.dataKey.toString(),
    key: child.props.dataKey.toString(),
  }));

  const [checkedVisibleColumns, setCheckedVisibleColumns] = useState<Set<TrackerColumnKeys>>(
    new Set(
      columns
        .filter((c) => selectedCols.has(c.props.dataKey))
        .map((c) => c.props.dataKey.toString()),
    ),
  );

  const [visibleColumns, setVisibleColumns] = useState<Array<JSX.Element>>(
    columns.filter((column) => checkedVisibleColumns.has(column.props.dataKey)),
  );

  useEffect(() => {
    if (showShippingCol) {
      setCheckedVisibleColumns(checkedVisibleColumns.add(shippingCol.props.dataKey));
    } else {
      checkedVisibleColumns.delete(shippingCol.props.dataKey);
      setCheckedVisibleColumns(checkedVisibleColumns);
    }
    setVisibleColumns(columns.filter((column) => checkedVisibleColumns.has(column.props.dataKey)));
  }, [showShippingCol, trackerTags, shippingInfo]);

  const totalMinWidth = visibleColumns.reduce(
    (sum, col) => sum + (col.props.minWidth + 30 || 0),
    0,
  );

  return (
    <Container>
      <Table<ManagedTracker, ObjectId>
        minWidth={totalMinWidth}
        data={trackers}
        stripped
        hover
        noDataPlaceholder={noDataPlaceholder}
        draggable
        selectionIds={selectedTrackerIds}
        onSelectionChange={onSelectionChange}
        dragElementType={dragElementType}
        disabledRowIds={disabledTrackerIds}
        focusedRowId={inspectedTrackerId}
        onFocusRowChange={handleFocusRowChange}
        overscanRowCount={5}
        columnsCanBeHidden
        isSortingCheckboxCol
      >
        {visibleColumns}
      </Table>
      <TrackerCollumnsVisibilityControlButton
        columns={columns}
        columnLabels={columnLabels}
        checkedVisibleColumns={checkedVisibleColumns}
        setCheckedVisibleColumns={setCheckedVisibleColumns}
        setVisibleColumns={setVisibleColumns}
        dragElementType={dragElementType}
      />
    </Container>
  );
};

const Container = styled.div`
  height: 100%;
  overflow-x:auto 
`;

const Table = styled(DataTable)<{ minWidth: number }>`
  margin: unset;
  height: 100%;

  .table {
    border: unset;
    margin-bottom: unset;
    min-width: ${(props) => props.minWidth}px;
  }
` as unknown as typeof DataTable;
